import { Divider, Flex, Skeleton, Stack } from '@mantine/core';
import { PlainButton, Text, Title } from '@repo/foundations';
import { isNil, size } from 'lodash-es';
import { useCallback, useContext } from 'react';
import { useNavigate } from 'react-router';
import { AuthCard } from './AuthCard';
import { AuthLayoutBase } from './AuthLayouts/AuthLayoutBase';
import { AuthStepEmailComponent } from './AuthStepEmailComponent';
import { AuthStepSsoComponent } from './AuthStepSsoComponent';
import { AuthStepStartContext } from './AuthStepStartContext';
import { AuthTermsAndPolicy } from './AuthTermsAndPolicy';

export function AuthStepLogin() {
	const context = useContext(AuthStepStartContext);
	if (!context) {
		throw new Error(
			'AuthStepLogin must be used within a AuthStepStartContextProvider'
		);
	}
	const navigate = useNavigate();
	const { setError, setEmail, resetSSO, forceSSO, supportedMethods } = context;

	const handleSwitchToRegister = useCallback(() => {
		setError(null);
		navigate('/auth/signup');
	}, [navigate, setError]);

	const handleBackToLogin = useCallback(() => {
		setError(null);
		setEmail('');
		resetSSO();
	}, [setError, setEmail, resetSSO]);

	return (
		<AuthLayoutBase>
			<Flex gap="xl" direction="column">
				<AuthCard>
					<Title order={1}>Log in to Secoda</Title>
					<Stack mt="xl" spacing={0}>
						{isNil(supportedMethods) ? (
							<Stack spacing={'xs'}>
								<Skeleton height={36} radius="sm" />
								<Skeleton height={36} radius="sm" />
								<Skeleton height={36} radius="sm" />
								<Skeleton height={36} radius="sm" />
								<Skeleton height={36} radius="sm" />
							</Stack>
						) : (
							<>
								{supportedMethods?.includes('email') && (
									<>
										<AuthStepEmailComponent cta="Continue with email" />
										{size(supportedMethods) > 1 && (
											<Divider
												hidden={!!forceSSO}
												my="md"
												label={
													<Text
														color="text/secondary/default"
														size="sm"
														px="3xs"
													>
														or
													</Text>
												}
												labelPosition="center"
											/>
										)}
									</>
								)}
								<AuthStepSsoComponent supportedMethods={supportedMethods} />
							</>
						)}
						<Stack spacing="xl" mt="xl">
							<AuthTermsAndPolicy />
							{forceSSO ? (
								<PlainButton onClick={handleBackToLogin} size="sm">
									Back to login
								</PlainButton>
							) : (
								<Flex gap="xs" justify="center">
									<Text size="sm" color="text/secondary/default">
										Don&apos;t have an account?
									</Text>
									<PlainButton onClick={handleSwitchToRegister} size="sm">
										Sign up
									</PlainButton>
								</Flex>
							)}
						</Stack>
					</Stack>
				</AuthCard>
			</Flex>
		</AuthLayoutBase>
	);
}
